"use client";

/* eslint-disable @next/next/no-img-element */
import Image from "next/image";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ImageOff,
  Loader2,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type TouchEvent,
  type WheelEvent,
} from "react";
import { canOptimizeImageHost } from "@/lib/storeImage";
import { ProductPhotoFallback } from "@/components/ProductPhotoFallback";
import {
  GALLERY_SETTLE_MAX_RETRIES,
  GALLERY_SETTLE_MS,
  edgeSettleDecision,
  slideIndexFromScroll,
} from "@/lib/gallerySwipe";

/**
 * Batas jumlah slide yang masih diberi rel titik. Form admin mematok 10 foto
 * per produk, jadi selama galeri cuma berisi foto produk rel titik tak pernah
 * melewati lebar layar. Sejak foto per-opsi variasi ikut masuk
 * (`buildProductGallery`) jumlahnya tak lagi terbatas — produk grosir fashion
 * dengan puluhan warna bisa punya 40+ slide, dan 40 titik + jaraknya lebih
 * lebar dari layar HP 360px. Di atas ambang ini titik diganti penghitung
 * "n / N"; panah kiri-kanan & swipe tetap sama.
 */
const MAX_DOT_SLIDES = 12;

/**
 * `sizes` foto galeri. DIEKSPOR supaya slide pratinjau produk berikutnya
 * (`NextProductPeek`) meminta varian gambar yang PERSIS sama: begitu pembeli
 * pindah produk, slide 0 halaman tujuan (cover yang sama) terambil dari cache
 * browser, tanpa spinner.
 */
export const GALLERY_IMAGE_SIZES = "(max-width: 480px) 100vw, 448px";

/** Lama petunjuk sekali-pakai tampil sebelum hilang sendiri. */
const HINT_VISIBLE_MS = 3500;

/** Diam di foto ujung selama ini (setelah menyentuh galeri) = niat → warm-up. */
const EDGE_DWELL_MS = 600;

/** Seretan melewati foto ujung sejauh ini (px) = niat menuju slide pratinjau. */
const EDGE_INTENT_PX = 8;

const SCROLLER_CLASS =
  "flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
// Terkunci selama pindah produk berjalan. `pointer-events-none`, BUKAN
// `overflow-x-hidden`: yang terakhir bisa mereset `scrollLeft` di iOS dan
// melempar pembeli kembali ke foto sebelum halaman tujuan tiba.
const SCROLLER_LOCKED_CLASS = `${SCROLLER_CLASS} pointer-events-none`;

/**
 * Kelas `<section>` galeri — string LITERAL utuh per kombinasi (Tailwind JIT
 * hanya membaca literal di sumber). `clip` = galeri ber-scroller (overflow
 * disembunyikan); tanpa scroller (produk tanpa foto) tak perlu.
 */
function sectionClass(tone: "light" | "dark", fill: boolean, clip: boolean): string {
  if (tone === "dark") {
    if (fill) return clip ? "relative h-full w-full overflow-hidden bg-neutral-900" : "relative h-full w-full bg-neutral-900";
    return clip ? "relative aspect-[4/5] overflow-hidden bg-neutral-900" : "relative aspect-[4/5] bg-neutral-900";
  }
  if (fill) return clip ? "relative h-full w-full overflow-hidden bg-shopeeSoft/60" : "relative h-full w-full bg-shopeeSoft/60";
  return clip ? "relative aspect-[4/5] overflow-hidden bg-shopeeSoft/60" : "relative aspect-[4/5] bg-shopeeSoft/60";
}

/** Slide pratinjau produk lain di ujung galeri (lihat `leadingSlide`/`trailingSlide`). */
export type GalleryEdge = "leading" | "trailing";

/**
 * Kendali imperatif galeri untuk pemanggil (ProductDetail): melompatkan slider
 * ke slide tertentu saat pembeli mengklik opsi variasi yang punya foto.
 *
 * IMPERATIF, bukan prop `activeIndex` terkendali: pembeli boleh menggeser
 * slider setelah mengklik sebuah opsi, lalu mengklik opsi yang SAMA lagi untuk
 * kembali ke fotonya. Sebagai prop, klik kedua itu tak mengubah nilai apa pun
 * sehingga slider diam di tempat — persis yang tidak diharapkan pembeli.
 */
export type ProductGalleryControl = {
  /**
   * Geser slider ke slide `index` SAJA — halaman tidak ikut bergerak. Dipakai
   * navigasi di dalam galeri sendiri (panah & titik) dan opsi variasi ber-foto
   * (`focusOptionPhoto` di ProductDetail). Galeri sengaja tidak menggulir
   * halaman ke dirinya sendiri (keputusan pemilik 2026-10-05).
   */
  goTo: (index: number) => void;
};

type Geometry = {
  /** 1 bila slide pratinjau AWAL terpasang (foto bergeser satu slide). */
  offset: number;
  /** Jumlah slide foto (produk tanpa foto ber-slide ujung = 1 slide cadangan). */
  photoCount: number;
  hasLeading: boolean;
  hasTrailing: boolean;
  locked: boolean;
};

/**
 * Galeri foto produk yang bisa DI-SWIPE ke samping (native touch scroll +
 * scroll-snap) dan menampilkan foto TANPA terpotong (`object-contain` di atas
 * latar lembut). Tiap slide punya indikator loading sendiri: saat di-swipe ke
 * foto yang belum ter-load, spinner tampil sampai foto selesai dimuat (atau
 * ikon rusak bila gagal). Cached image dideteksi lewat ref agar spinner tak
 * menggantung selamanya (onLoad kadang tak terpicu untuk gambar dari cache).
 *
 * FEED GESER (opsional): `leadingSlide`/`trailingSlide` memasang slide
 * pratinjau produk lain di awal/ujung. Di foto terakhir scroller sudah mentok —
 * menggeser lebih jauh tak memicu event apa pun — jadi ujungnya diberi slide
 * sungguhan untuk DIDARATI snap; begitu mendarat (jari terangkat, posisi
 * diam, ada gestur eksplisit — `edgeSettleDecision`) `onEdgeSettle` dipanggil
 * dan pemanggil pindah produk. Tanpa kedua prop itu perilakunya persis galeri
 * lama.
 */
export function ProductImageGallery({
  images,
  alt,
  onActiveChange,
  storeLogoFallback = true,
  controlRef,
  leadingSlide,
  trailingSlide,
  leadingLabel = "Sebelumnya",
  trailingLabel = "Berikutnya",
  onEdgeSettle,
  onEdgeIntent,
  edgeLocked = false,
  trailingHint = false,
  onTrailingHintSeen,
  tone = "light",
  fill = false,
  priorityFirstPhoto = true,
}: {
  images: string[];
  alt: string;
  /** Dipanggil saat foto aktif berganti (dipakai parent utk share foto). */
  onActiveChange?: (index: number) => void;
  /**
   * Produk tanpa foto: tampilkan logo toko sebagai gambar default. Mode
   * katalog reseller WAJIB false — logo toko induk harus disembunyikan
   * (white-label); ProductDetail meneruskan `!catalogMode`.
   */
  storeLogoFallback?: boolean;
  /** Kotak yang diisi galeri dengan kendalinya (lihat ProductGalleryControl). */
  controlRef?: RefObject<ProductGalleryControl | null>;
  /** Pratinjau produk SEBELUMNYA, di depan foto pertama. */
  leadingSlide?: ReactNode;
  /** Pratinjau produk BERIKUTNYA, sesudah foto terakhir. */
  trailingSlide?: ReactNode;
  /** Teks pil di foto pertama (menuju `leadingSlide`). */
  leadingLabel?: string;
  /** Teks pil di foto terakhir (menuju `trailingSlide`). */
  trailingLabel?: string;
  /** Snap MENDARAT di slide ujung karena gestur pembeli → pindah produk. */
  onEdgeSettle?: (edge: GalleryEdge) => void;
  /** Niat menuju slide ujung (menyeret masuk / diam di foto ujung) → warm-up. */
  onEdgeIntent?: (edge: GalleryEdge) => void;
  /** Kunci scroller selama pindah produk berlangsung. */
  edgeLocked?: boolean;
  /** Tampilkan petunjuk sekali-pakai saat foto terakhir pertama kali dicapai. */
  trailingHint?: boolean;
  /** Petunjuk baru saja ditampilkan (pemanggil menandainya sudah dilihat). */
  onTrailingHintSeen?: () => void;
  /**
   * `"dark"` = latar gelap (halaman Jelajah, feed layar penuh). Bawaan
   * `"light"` = latar lembut warna tema, persis halaman produk.
   */
  tone?: "light" | "dark";
  /**
   * `true` = galeri MENGISI tinggi pembungkusnya (item Jelajah yang tingginya
   * mengikuti layar) alih-alih rasio 4:5 tetap halaman produk.
   */
  fill?: boolean;
  /**
   * Foto pertama dimuat prioritas tinggi (kandidat LCP halaman produk).
   * Jelajah mematikannya untuk semua item kecuali yang pertama — puluhan
   * galeri dalam satu halaman tak boleh semuanya mengaku "paling penting".
   */
  priorityFirstPhoto?: boolean;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);

  const count = images.length;
  const hasLeadingProp = Boolean(leadingSlide);
  const hasTrailing = Boolean(trailingSlide);
  const hasEdges = hasLeadingProp || hasTrailing;
  // Produk tanpa foto yang punya slide ujung tetap butuh SATU slide di tengah
  // (logo toko / kotak polos) — dari sanalah pembeli menggeser.
  const photoCount = count === 0 && hasEdges ? 1 : count;

  // Slide pratinjau AWAL baru dipasang saat scroller DIAM (efek di bawah):
  // menyisipkan slide di depan menggeser semua foto satu slide, dan
  // mengompensasinya di tengah gestur pembeli membuat iOS melompat.
  const [leadingActive, setLeadingActive] = useState(false);
  const showLeading = hasLeadingProp && leadingActive;
  const offset = showLeading ? 1 : 0;

  // Posisi = indeks slide mentah BESERTA offset saat ia diukur, supaya indeks
  // foto tetap benar di render transisi saat slide awal baru disisipkan.
  const [position, setPosition] = useState({ slide: 0, offset: 0 });
  // Scroller sedang di antara dua snap (diseret / dianimasikan). Pil ujung &
  // petunjuk disembunyikan selama itu supaya pratinjau yang masuk tak
  // tertimpa. Diukur dari POSISI, bukan dari sentuhan: menyembunyikan pil
  // saat `touchstart` akan membatalkan ketukan pada pil itu sendiri.
  const [moving, setMoving] = useState(false);
  const lastPhoto = Math.max(photoCount - 1, 0);
  const active = Math.max(0, Math.min(lastPhoto, position.slide - position.offset));
  const onLeadingSlide = showLeading && position.offset === 1 && position.slide === 0;
  const onTrailingSlide =
    hasTrailing && position.slide === position.offset + photoCount;
  const onEdgeSlide = onLeadingSlide || onTrailingSlide;
  const atFirstPhoto = active === 0;
  const atLastPhoto = active === lastPhoto;

  // ── Status gestur (ref: dibaca handler & timer tanpa memicu render) ────────
  const touchingRef = useRef(false);
  /** `performance.now()` gestur eksplisit terakhir; 0 = belum pernah. */
  const lastGestureRef = useRef(0);
  const lastScrollAtRef = useRef(0);
  const settleTimerRef = useRef<number | null>(null);
  const settleRetriesRef = useRef(0);
  /** Indeks foto terakhir yang terukur dari posisi scroll. */
  const photoRef = useRef(0);
  const geometryRef = useRef<Geometry>({
    offset: 0,
    photoCount,
    hasLeading: false,
    hasTrailing,
    locked: edgeLocked,
  });
  const onEdgeSettleRef = useRef(onEdgeSettle);
  const onEdgeIntentRef = useRef(onEdgeIntent);
  const onTrailingHintSeenRef = useRef(onTrailingHintSeen);

  // Layout effect, dan DIDEKLARASIKAN sebelum kompensasi di bawah: geometri
  // harus sudah mencerminkan DOM yang baru di-commit sebelum event scroll
  // berikutnya dibaca.
  useLayoutEffect(() => {
    geometryRef.current = {
      offset,
      photoCount,
      hasLeading: showLeading,
      hasTrailing,
      locked: edgeLocked,
    };
    onEdgeSettleRef.current = onEdgeSettle;
    onEdgeIntentRef.current = onEdgeIntent;
    onTrailingHintSeenRef.current = onTrailingHintSeen;
  });

  const markGesture = useCallback(() => {
    lastGestureRef.current = performance.now();
  }, []);

  // Posisi diam → mendarat di slide ujung? (`edgeSettleDecision`, diuji murni.)
  const checkSettle = useCallback(() => {
    settleTimerRef.current = null;
    const el = scrollerRef.current;
    const geometry = geometryRef.current;
    if (!el || geometry.locked || (!geometry.hasLeading && !geometry.hasTrailing)) {
      return;
    }
    const decision = edgeSettleDecision({
      scrollLeft: el.scrollLeft,
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
      hasLeading: geometry.hasLeading,
      hasTrailing: geometry.hasTrailing,
      touching: touchingRef.current,
      msSinceGesture: lastGestureRef.current
        ? performance.now() - lastGestureRef.current
        : Number.POSITIVE_INFINITY,
    });
    if (decision === "wait") {
      if (settleRetriesRef.current < GALLERY_SETTLE_MAX_RETRIES) {
        settleRetriesRef.current += 1;
        settleTimerRef.current = window.setTimeout(checkSettle, GALLERY_SETTLE_MS);
      }
      return;
    }
    settleRetriesRef.current = 0;
    if (decision === "open-trailing") onEdgeSettleRef.current?.("trailing");
    else if (decision === "open-leading") onEdgeSettleRef.current?.("leading");
  }, []);

  const scheduleSettle = useCallback(
    (resetRetries = false) => {
      if (resetRetries) settleRetriesRef.current = 0;
      if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = window.setTimeout(checkSettle, GALLERY_SETTLE_MS);
    },
    [checkSettle]
  );

  // Hitung indeks aktif dari posisi scroll (di-throttle via rAF agar mulus).
  const handleScroll = useCallback(() => {
    lastScrollAtRef.current = performance.now();
    const el = scrollerRef.current;
    const geometry = geometryRef.current;
    if (el && el.clientWidth > 0 && touchingRef.current) {
      // Jari menyeret MELEWATI foto ujung, masuk ke pratinjau → niat.
      const firstPhotoLeft = geometry.offset * el.clientWidth;
      const lastPhotoLeft = (geometry.offset + geometry.photoCount - 1) * el.clientWidth;
      if (geometry.hasTrailing && el.scrollLeft > lastPhotoLeft + EDGE_INTENT_PX) {
        onEdgeIntentRef.current?.("trailing");
      } else if (geometry.hasLeading && el.scrollLeft < firstPhotoLeft - EDGE_INTENT_PX) {
        onEdgeIntentRef.current?.("leading");
      }
    }
    if (geometry.hasLeading || geometry.hasTrailing) scheduleSettle();

    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      const node = scrollerRef.current;
      if (!node || node.clientWidth === 0) return;
      const g = geometryRef.current;
      const lastSlide = g.offset + g.photoCount - 1 + (g.hasTrailing ? 1 : 0);
      const slide = slideIndexFromScroll(node.scrollLeft, node.clientWidth, lastSlide);
      photoRef.current = Math.max(0, Math.min(g.photoCount - 1, slide - g.offset));
      setPosition((prev) =>
        prev.slide === slide && prev.offset === g.offset
          ? prev
          : { slide, offset: g.offset }
      );
      const drift = Math.abs(node.scrollLeft - slide * node.clientWidth);
      setMoving(drift > EDGE_INTENT_PX);
    });
  }, [scheduleSettle]);

  const handleTouchStart = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      touchingRef.current = event.touches.length > 0;
      markGesture();
    },
    [markGesture]
  );

  // Jari terangkat = saat keputusan diambil. Retry direset: menahan jari lama
  // di atas pratinjau tak boleh menghabiskan jatah pengecekan.
  const handleTouchEnd = useCallback(
    (event: TouchEvent<HTMLDivElement>) => {
      touchingRef.current = event.touches.length > 0;
      markGesture();
      if (geometryRef.current.hasLeading || geometryRef.current.hasTrailing) {
        scheduleSettle(true);
      }
    },
    [markGesture, scheduleSettle]
  );

  const handleWheel = useCallback(
    (event: WheelEvent<HTMLDivElement>) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) markGesture();
    },
    [markGesture]
  );

  // Tab TIDAK termasuk: fokus yang menggulung scroller ke pratinjau hanya
  // memperlihatkannya; Enter pada tautannya yang membuka.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight" ||
        event.key === "Home" ||
        event.key === "End" ||
        event.key === "PageUp" ||
        event.key === "PageDown"
      ) {
        markGesture();
      }
    },
    [markGesture]
  );

  // Pasang slide pratinjau awal hanya saat scroller diam & tak disentuh.
  useEffect(() => {
    if (!hasLeadingProp) {
      setLeadingActive(false);
      return;
    }
    if (leadingActive) return;
    let timer: number | null = null;
    const attempt = () => {
      const idle =
        !touchingRef.current &&
        performance.now() - lastScrollAtRef.current >= GALLERY_SETTLE_MS;
      if (idle) setLeadingActive(true);
      else timer = window.setTimeout(attempt, GALLERY_SETTLE_MS);
    };
    attempt();
    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [hasLeadingProp, leadingActive]);

  // Kompensasi saat slide awal dipasang/dilepas — SEBELUM paint, jadi foto yang
  // sedang dilihat tak berkedip. Posisi ABSOLUT, bukan `+= lebar`: sebagian
  // browser me-re-snap sendiri ke elemen yang sama setelah layout berubah,
  // dan penambahan relatif akan menggeser dua kali.
  const leadingShownRef = useRef(false);
  useLayoutEffect(() => {
    const was = leadingShownRef.current;
    leadingShownRef.current = showLeading;
    if (was === showLeading) return;
    const el = scrollerRef.current;
    if (!el || el.clientWidth === 0) return;
    const nextOffset = showLeading ? 1 : 0;
    el.scrollLeft = (photoRef.current + nextOffset) * el.clientWidth;
    setPosition({ slide: photoRef.current + nextOffset, offset: nextOffset });
  }, [showLeading]);

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) window.cancelAnimationFrame(rafRef.current);
      if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
    };
  }, []);

  const goTo = useCallback((index: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const geometry = geometryRef.current;
    const clamped = Math.max(0, Math.min(geometry.photoCount - 1, index));
    el.scrollTo({
      left: (clamped + geometry.offset) * el.clientWidth,
      behavior: "smooth",
    });
  }, []);

  // Pil "‹ Sebelumnya"/"Berikutnya ›": ketukannya gestur eksplisit, jadi
  // begitu scroll-nya mendarat di pratinjau, produknya terbuka.
  const goToEdge = useCallback(
    (edge: GalleryEdge) => {
      const el = scrollerRef.current;
      if (!el) return;
      markGesture();
      const geometry = geometryRef.current;
      const left =
        edge === "leading" ? 0 : (geometry.offset + geometry.photoCount) * el.clientWidth;
      el.scrollTo({ left, behavior: "smooth" });
      onEdgeIntentRef.current?.(edge);
    },
    [markGesture]
  );

  // Kendali dititipkan lewat effect (bukan saat render) supaya galeri yang
  // sudah tak terpasang tak meninggalkan `goTo` basi yang menggeser scroller
  // milik produk lain saat pembeli berpindah halaman.
  useEffect(() => {
    if (!controlRef) return;
    controlRef.current = { goTo };
    return () => {
      controlRef.current = null;
    };
  }, [controlRef, goTo]);

  const leadingPill = showLeading && atFirstPhoto && !onEdgeSlide && !moving;
  const trailingPill = hasTrailing && atLastPhoto && !onEdgeSlide && !moving;

  // Warm-up karena DIAM di foto ujung — hanya bila pembeli sudah menyentuh
  // galeri (pengunjung yang sekadar membuka halaman tak memicu apa pun).
  useEffect(() => {
    if (onEdgeSlide) return;
    const edge: GalleryEdge | null =
      hasTrailing && atLastPhoto ? "trailing" : showLeading && atFirstPhoto ? "leading" : null;
    if (!edge) return;
    const timer = window.setTimeout(() => {
      if (lastGestureRef.current) onEdgeIntentRef.current?.(edge);
    }, EDGE_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [hasTrailing, showLeading, atLastPhoto, atFirstPhoto, onEdgeSlide]);

  // Petunjuk sekali-pakai: sekali per galeri, saat foto terakhir pertama kali
  // dicapai. Timer tak ikut dibatalkan saat pembeli bergeser — ia hanya
  // menyembunyikan; syarat tampilnya tetap `trailingPill` di render.
  const [hintVisible, setHintVisible] = useState(false);
  const hintShownRef = useRef(false);
  const hintTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!trailingHint || !trailingPill || hintShownRef.current) return;
    hintShownRef.current = true;
    setHintVisible(true);
    onTrailingHintSeenRef.current?.();
    hintTimerRef.current = window.setTimeout(() => setHintVisible(false), HINT_VISIBLE_MS);
  }, [trailingHint, trailingPill]);
  useEffect(() => {
    return () => {
      if (hintTimerRef.current !== null) window.clearTimeout(hintTimerRef.current);
    };
  }, []);

  if (count === 0 && !hasEdges) {
    return (
      <section className={sectionClass(tone, fill, false)}>
        {storeLogoFallback ? (
          <ProductPhotoFallback sizes={GALLERY_IMAGE_SIZES} />
        ) : null}
      </section>
    );
  }

  const showPhotoControls = count > 1 && !onEdgeSlide;

  return (
    <section className={sectionClass(tone, fill, true)}>
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        className={edgeLocked ? SCROLLER_LOCKED_CLASS : SCROLLER_CLASS}
      >
        {showLeading ? (
          <div key="leading-edge" className="relative h-full w-full flex-none snap-start">
            {leadingSlide}
          </div>
        ) : null}
        {count === 0 ? (
          <div key="photo-fallback" className="relative h-full w-full flex-none snap-start">
            {storeLogoFallback ? (
              <ProductPhotoFallback sizes={GALLERY_IMAGE_SIZES} />
            ) : null}
          </div>
        ) : (
          images.map((src, index) => (
            <GallerySlide
              key={`${src}-${index}`}
              src={src}
              alt={alt}
              priority={priorityFirstPhoto && index === 0}
              tone={tone}
              // Fling keras dari tengah galeri berhenti di foto ujung, tak
              // langsung terbang ke produk lain.
              snapStop={
                (index === 0 && showLeading) || (index === count - 1 && hasTrailing)
              }
            />
          ))
        )}
        {hasTrailing ? (
          <div key="trailing-edge" className="relative h-full w-full flex-none snap-start">
            {trailingSlide}
          </div>
        ) : null}
      </div>

      {leadingPill ? (
        <button
          type="button"
          aria-label="Produk sebelumnya"
          onClick={() => goToEdge("leading")}
          className="absolute left-3 top-1/2 flex h-9 -translate-y-1/2 items-center gap-0.5 rounded-full bg-white/90 pl-1.5 pr-3 text-[12px] font-extrabold text-ink shadow-sm"
        >
          <ChevronLeft size={17} />
          {leadingLabel}
        </button>
      ) : showPhotoControls ? (
        <button
          type="button"
          aria-label="Foto sebelumnya"
          onClick={() => {
            markGesture();
            goTo(active - 1);
          }}
          disabled={active === 0}
          className="absolute left-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-ink shadow-sm transition disabled:opacity-0"
        >
          <ChevronLeft size={19} />
        </button>
      ) : null}
      {trailingPill ? (
        <button
          type="button"
          aria-label="Produk berikutnya"
          onClick={() => goToEdge("trailing")}
          className="absolute right-3 top-1/2 flex h-9 -translate-y-1/2 items-center gap-0.5 rounded-full bg-white/90 pl-3 pr-1.5 text-[12px] font-extrabold text-ink shadow-sm"
        >
          {trailingLabel}
          <ChevronRight size={17} />
        </button>
      ) : showPhotoControls ? (
        <button
          type="button"
          aria-label="Foto berikutnya"
          onClick={() => {
            markGesture();
            goTo(active + 1);
          }}
          disabled={active === count - 1}
          className="absolute right-3 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full bg-white/90 text-ink shadow-sm transition disabled:opacity-0"
        >
          <ChevronRight size={19} />
        </button>
      ) : null}
      {showPhotoControls ? (
        count <= MAX_DOT_SLIDES ? (
          <div className="pointer-events-none absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((src, index) => (
              <button
                key={`dot-${src}-${index}`}
                type="button"
                aria-label={`Foto ${index + 1}`}
                onClick={() => goTo(index)}
                className={`pointer-events-auto h-1.5 rounded-full transition-all ${
                  active === index ? "w-5 bg-white" : "w-1.5 bg-white/60"
                }`}
              />
            ))}
          </div>
        ) : (
          <div className="absolute bottom-3 right-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-extrabold text-ink shadow-sm">
            {active + 1} / {count}
          </div>
        )
      ) : null}
      {hintVisible && trailingPill ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-10 flex justify-center">
          <div className="flex items-center gap-1.5 rounded-full bg-ink/80 px-3.5 py-2 text-[12px] font-bold text-white shadow-lg">
            <ChevronsLeft size={16} className="animate-feed-nudge motion-reduce:animate-none" />
            Geser untuk produk berikutnya
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Satu slide galeri: foto `object-contain` (tak terpotong) + overlay loading /
 * error. Status load dideteksi via onLoad DAN pengecekan `complete` saat ref
 * terpasang (menutup celah gambar dari cache yang tak memicu onLoad).
 */
function GallerySlide({
  src,
  alt,
  priority,
  snapStop,
  tone,
}: {
  src: string;
  alt: string;
  priority: boolean;
  /** `scroll-snap-stop: always` — dipasang di foto ujung yang bertetangga slide pratinjau. */
  snapStop: boolean;
  tone: "light" | "dark";
}) {
  const [status, setStatus] = useState<"loading" | "loaded" | "error">(
    "loading"
  );

  const markLoaded = useCallback(() => setStatus("loaded"), []);
  const markError = useCallback(() => setStatus("error"), []);

  // Ref callback: bila elemen <img> sudah lengkap saat terpasang (cache),
  // langsung tandai loaded tanpa menunggu event onLoad yang mungkin terlewat.
  const imgRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node && node.complete && node.naturalWidth > 0) markLoaded();
    },
    [markLoaded]
  );

  const optimizable = canOptimizeImageHost(src);

  return (
    <div
      className={
        snapStop
          ? "relative h-full w-full flex-none snap-start snap-always"
          : "relative h-full w-full flex-none snap-start"
      }
    >
      {status !== "loaded" ? (
        <div
          className={
            tone === "dark"
              ? "absolute inset-0 z-10 grid place-items-center bg-neutral-900"
              : "absolute inset-0 z-10 grid place-items-center bg-shopeeSoft/60"
          }
        >
          {status === "error" ? (
            <div className="flex flex-col items-center gap-1 text-muted">
              <ImageOff size={26} />
              <span className="text-[11px] font-bold">Gagal memuat foto</span>
            </div>
          ) : (
            <Loader2 size={26} className="animate-spin text-shopee" />
          )}
        </div>
      ) : null}

      {optimizable ? (
        <Image
          ref={imgRef}
          src={src}
          alt={alt}
          fill
          sizes={GALLERY_IMAGE_SIZES}
          priority={priority}
          className="object-contain"
          onLoad={markLoaded}
          onError={markError}
        />
      ) : (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          className="absolute inset-0 h-full w-full object-contain"
          onLoad={markLoaded}
          onError={markError}
        />
      )}
    </div>
  );
}
